import { Injectable } from '@angular/core';
import Mexp from 'math-expression-evaluator';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from '@mona/api';
import { User } from '@mona/auth';
import { Encounter, ExternalResource } from '@mona/models';
import { transformApiExternalResources } from './transforms/external-resources.transform';

/**
 * INFO: DailyGoalsApi
 */
@Injectable({ providedIn: 'root' })
export class ExternalResourcesApi {
    private readonly apiBase = '/api/external-links';

    /**
     * Constructor
     *
     * @param http HttpClient
     */
    constructor(private http: HttpService) {}

    /**
     * Loads daily goals
     * @param encounter
     * @param user
     */
    getExternalResources(encounter: Encounter, user: User): Observable<ExternalResource[]> {
        return this.http.get<ExternalResource[]>(this.apiBase, { params: { encounter_id: encounter.id } }).pipe(
            map(result => transformApiExternalResources(result)),
            map(data => this.convertObjectToParameters(data, encounter, user)),
        );
    }

    private convertObjectToParameters(data: ExternalResource[], encounter: Encounter, user: User): ExternalResource[] {
        const encounterData = {
            patientId: encounter.patient.id,
            encounterId: encounter.id,
            patientNumber: encounter.patient.patientNumber,
            caseId: encounter.caseId,
            gender: encounter.patient.gender,
            firstName: encounter.patient.firstName,
            lastName: encounter.patient.lastName,
            wardId: encounter.wardId,
            bedId: encounter.bedId,
            dateOfBirth: encounter.patient.dateOfBirth,
            hospitalCaseId: encounter.hospitalCaseId,
            practitionerId: user.id,
            practitionerUsername: user.username,
            practitionerRoles: Array.isArray(user.role) ? user.role.join(';') : (user.role ?? ''),
            practitionerFirstName: user.firstName,
            practitionerLastName: user.lastName,
            practitionerPrefix: user.prefix,
        };

        return data.map(item => {
            let changedUrl = item.url;

            Object.keys(encounterData).forEach(key => {
                changedUrl = changedUrl.replaceAll(`{{${key}}}`, encounterData[key]);
            });

            const mathParams = changedUrl.matchAll(/(?<=<)[^<>]+(?=>)/g);

            const mathParamsArray = Array.from(mathParams || []);
            const mexp = new Mexp();

            mathParamsArray.forEach(param => {
                try {
                    const expression = param && String(param[0]).trim();
                    const result = mexp.eval(expression);

                    if (String(result)) {
                        const originalPattern = `<${param[0]}>`;
                        changedUrl = changedUrl.replace(originalPattern, String(result));
                    }
                } catch (error) {
                    console.warn('Error while calculating math expression:', {
                        expression: param[0],
                        error: error.message,
                    });
                }
            });

            return { ...item, url: changedUrl };
        });
    }
}
